/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable @next/next/no-img-element */
import React, { FC, useContext } from "react";
import { GetServerSideProps } from "next";
import { StructuredText } from "react-datocms";
import RockstarFooter from "components/RockstarFooter";
import RockstarHeader from "components/RockstarHeader";
import { LogContext } from "context/log";
import { AthleteFragment, getSdk } from "services/datocms/generated";
import { createDatoClient } from "services/datocms";
import DatoHeadTagsRenderer from "components/DatoHeadTagsRenderer";
import RockstarRelatedProducts from "components/RockstarRelatedProducts";
import TopCtaBanner from "components/TopCtaBanner";
import { PAGE_TYPE_ENUM } from "@pepdirect/shared/types";
import { Button } from "@pepdirect/ui/button";

interface AthleteDetailPageProps {
  athlete?: AthleteFragment | null;
}

export const getServerSideProps: GetServerSideProps<
  AthleteDetailPageProps
> = async (context) => {
  const slug = context?.params?.slug;

  try {
    const { athlete } = await getSdk(
      createDatoClient(context.preview)
    ).AthleteBySlug({ slug: slug as string });

    return {
      props: {
        athlete: athlete,
      },
    };
  } catch (e) {
    return {
      props: {
        athlete: null,
      },
    };
  }
};

const AthleteDetails: FC<AthleteDetailPageProps> = ({ athlete }) => {
  const { useLogPageView } = useContext(LogContext);
  useLogPageView({
    pageType: PAGE_TYPE_ENUM.other,
    pageCategory: undefined,
    pageTitle: athlete?.title || "",
    id: athlete?.id,
  });

  return (
    <>
      {athlete?.seoTagsGenerator && (
        <DatoHeadTagsRenderer datoSeoTag={athlete?.seoTagsGenerator[0]} />
      )}
      <div id="page" className="site">
        <a className="skip-link screen-reader-text" href="#content">
          Skip to content
        </a>

        <div className="site-header-block">
          <TopCtaBanner />
          <RockstarHeader />
        </div>

        <div id="content" className="site-content">
          <div id="primary" className="content-area">
            <main id="main" className="site-main" role="main">
              <article
                id="post-1216"
                className="post-1216 athletes type-athletes status-publish has-post-thumbnail hentry tag-chadkerley AthleteCategories-bmx entry"
              >
                <div className="article-post focus">
                  <div className="entry-content focus">
                    <div>
                      <div
                        className="athlete-banner-block"
                        id="athlete-banner-block-block_61a8b4fc4e414"
                      >
                        <div className="container">
                          <div
                            className="athlete-banner"
                            style={{
                              backgroundImage: `url('${athlete?.banner?.[0]?.backgroundImage?.url}')`,
                            }}
                          >
                            <div className="athlete-banner-info">
                              <span>{athlete?.banner?.[0]?.topText}</span>
                              <h1>{athlete?.title}</h1>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className="athlete-facts-block"
                        id="athlete-facts-block-block_61a8b5124e415"
                      >
                        <div className="container">
                          <div className="athlete-facts">
                            <h2 className="facts-title focus">
                              FACTS &amp; STATS{" "}
                            </h2>
                            <ul className="facts-details">
                              {athlete?.athleteFacts?.[0]?.dateOfBirth ? (
                                <li>
                                  <label>Date of Birth</label>
                                  <p>
                                    {athlete?.athleteFacts?.[0]?.dateOfBirth}
                                  </p>
                                </li>
                              ) : (
                                <></>
                              )}

                              {athlete?.athleteFacts?.[0]?.nationality ? (
                                <li>
                                  <label>NATIONALITY</label>
                                  <p>
                                    {athlete?.athleteFacts?.[0]?.nationality}
                                  </p>
                                </li>
                              ) : (
                                <></>
                              )}

                              {athlete?.athleteFacts?.[0]?.team ? (
                                <li>
                                  <label>TEAM</label>
                                  <p>{athlete?.athleteFacts?.[0]?.team}</p>
                                </li>
                              ) : (
                                <></>
                              )}

                              {athlete?.athleteFacts?.[0]?.location ? (
                                <li>
                                  <label>LOCATION</label>
                                  <p>{athlete?.athleteFacts?.[0]?.location}</p>
                                </li>
                              ) : (
                                <></>
                              )}

                              {athlete?.athleteFacts?.[0]?.favoriteRockstar ? (
                                <li>
                                  <label>FAVORITE ROCKSTAR</label>
                                  <p>
                                    {
                                      athlete?.athleteFacts?.[0]
                                        ?.favoriteRockstar
                                    }
                                  </p>
                                </li>
                              ) : (
                                <></>
                              )}

                              {athlete?.athleteFacts?.[0]?.topTitleWon
                                ?.value ? (
                                <li>
                                  <label>TOP TITLE WON</label>
                                  <StructuredText
                                    data={
                                      athlete?.athleteFacts?.[0]?.topTitleWon
                                        ?.value
                                    }
                                  />
                                </li>
                              ) : (
                                <></>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div
                        className="athlete-collectible-block"
                        id="athlete-collectible-block-block_61a8b5684e41c"
                      >
                        <div className="container">
                          <div className="athlete-collectible">
                            <h3 className="collectible-mobile-title focus">
                              Celebrate a win with a collectible
                            </h3>
                            <div className="collectible-img">
                              <img
                                src={athlete?.digitalCardPreview?.url}
                                alt={athlete?.title || ""}
                              />
                            </div>
                            <div className="collectible-details">
                              <div className="collectible-details-wrapper focus">
                                <h3>Celebrate a win with a collectible</h3>
                                <p>
                                  To celebrate {athlete?.title || ""}’s latest
                                  win, download this exclusive trading card as a
                                  digital background!
                                </p>
                                <a
                                  href={athlete?.digitalCard?.url}
                                  title="DOWNLOAD NOW"
                                >
                                  <Button className="focus-btn">
                                    DOWNLOAD NOW
                                  </Button>
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <RockstarRelatedProducts />
                    </div>
                  </div>
                </div>
              </article>
            </main>
          </div>
        </div>

        <RockstarFooter />
      </div>
    </>
  );
};

export default AthleteDetails;
